/**
 * useUrlState - React Router v6 兼容的 URL 状态管理 hook
 * 替代 @ahooksjs/use-url-state（该库依赖已废弃的 useHistory）
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'umi';
import queryString from 'query-string';

const parseConfig = {
  skipNull: true,
  skipEmptyString: true,
  parseNumbers: false,
  parseBooleans: false,
};

export interface UseUrlStateOptions {
  navigateMode?: 'push' | 'replace';
}

function useUrlState<T extends Record<string, any>>(
  initialState?: T | (() => T),
  options?: UseUrlStateOptions,
): [T, (s: Partial<T> | ((prevState: T) => Partial<T>)) => void] {
  const navigateMode = options?.navigateMode ?? 'push';
  const location = useLocation();
  const navigate = useNavigate();
  const [, forceUpdate] = useState(false);
  const initialStateRef = useRef(
    typeof initialState === 'function' ? (initialState as () => T)() : initialState || ({} as T),
  );

  const queryFromUrl = useMemo(
    () => queryString.parse(location.search, parseConfig) as Partial<T>,
    [location.search],
  );

  const targetQuery = useMemo(
    () => ({ ...initialStateRef.current, ...queryFromUrl } as T),
    [queryFromUrl],
  );

  const setState = useCallback(
    (s: Partial<T> | ((prevState: T) => Partial<T>)) => {
      const newQuery = typeof s === 'function' ? s(targetQuery) : s;
      const mergedQuery = { ...queryFromUrl, ...newQuery };
      const search = queryString.stringify(mergedQuery, parseConfig) || '';
      const newSearch = search ? `?${search}` : '';
      const to = { pathname: location.pathname, search: newSearch, hash: location.hash };

      forceUpdate((v) => !v);
      navigate(to, { replace: navigateMode === 'replace' });
    },
    [location.pathname, location.hash, queryFromUrl, targetQuery, navigate, navigateMode],
  );

  return [targetQuery, setState];
}

export default useUrlState;
